import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useSession, useUser } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import {
  Button,
  Input,
  Textarea,
  Tooltip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Accordion,
  AccordionItem,
} from "@heroui/react";
import { toast } from "react-toastify";
import { Link, useParams, useSearchParams } from "react-router-dom";
import EditParticipantModal from "./components/editParticipantModal";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  Archive,
  ArrowUpRight,
  ArrowSquareOut,
  CaretDown,
  ChatCircleSlash,
  Check,
  ChatTeardropText,
  Clock,
  DotsThreeVertical,
  EnvelopeOpen,
  MagnifyingGlass,
  NotePencil,
  PaperPlaneTilt,
  Plus,
  Trash,
  X,
} from "@phosphor-icons/react";
import AddConversationModal, {
  TemplateParam,
} from "./components/addConversationModal";
import WhatsAppTemplateModal from "./components/whatsappTemplateModal";
import DeleteConversationModal from "./components/deleteConversationModal";
import { debounce } from "lodash";
import ConversationList from "./components/conversationList";
import MessageActionsMenu from "./components/messageActionsMenu";
import SearchComponent from "shared/components/searchBar/components/searchComponent";
import { Resource, ResourceType } from "shared/types/resource";

interface Conversation {
  sid: string;
  friendlyName: string;
  dateCreated: string;
  attributes: string;
  dateUpdated: string;
  state: string;
  timers: Object;
}

interface Message {
  sid: string;
  author: string;
  delivery: any;
  body: string;
  dateCreated: string;
  media: {
    mediaUrl: string;
    content_type: string;
  }[];
}

export interface Participant {
  sid: string;
  identity: string;
  attributes: string;
  profilePicture?: string;
  messagingBinding: {
    address: string;
  };
}

const TwilioConversations: React.FC = () => {
  const { session } = useSession();
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<{ [key: string]: Message[] }>({});
  const [participants, setParticipants] = useState<{
    [key: string]: Participant[];
  }>({});
  const [loading, setLoading] = useState(true);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(
    undefined,
  );
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState<
    string | null
  >(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [showEditParticipantModal, setShowEditParticipantModal] =
    useState(false);
  const [newFriendlyName, setNewFriendlyName] = useState("");
  const [newMetadata, setNewMetadata] = useState("");
  const [newParticipantIdentity, setNewParticipantIdentity] = useState("");
  const [newParticipantAddress, setNewParticipantAddress] = useState("");
  const [newParticipantMetadata, setNewParticipantMetadata] = useState("");
  const [editingParticipant, setEditingParticipant] =
    useState<Participant | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [showDummyContent, setShowDummyContent] = useState(false);
  const [showAddConversationModal, setShowAddConversationModal] =
    useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [filter, setFilter] = useState("all");
  const [buildingFilter, setBuildingFilter] = useState("all");
  const [lastMessagePreviews, setLastMessagePreviews] = useState<{
    [key: string]: string;
  }>({});
  const [lastMessageDates, setLastMessageDates] = useState<{
    [key: string]: string;
  }>({});
  const [timers, setTimers] = useState<{ [key: string]: string }>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [readStatus, setReadStatus] = useState<{ [sid: string]: string }>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [chipFilter, setChipFilter] = useState<
    "all" | "unread" | "favorites" | "groups"
  >("all");
  const [selectedResource, setSelectedResource] = useState<Resource | null>(
    null,
  );
  const [groupBy, setGroupBy] = useState("none"); // 'none', 'building', 'unit', 'contact'
  const [showConversationDrawer, setShowConversationDrawer] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const [initialPhoneHandled, setInitialPhoneHandled] = useState(false);
  const [initialParticipantForModal, setInitialParticipantForModal] = useState<{
    phone: string;
    contactId?: string;
    companyId?: string;
    name?: string;
  } | null>(null);

  // Get initial params from URL (for deep linking from incasso etc.)
  const initialPhone = searchParams.get("phone");
  const initialContactId = searchParams.get("contactId");
  const initialCompanyId = searchParams.get("companyId");
  const initialName = searchParams.get("name");

  const { conversationSid } = useParams();

  const messageEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    console.log(selectedResource);
    fetchConversations();
  }, []);

  useEffect(() => {
    console.log("Conversations:", conversations);
    console.log("Selected Conversation:", selectedConversation);
    console.log("Messages:", messages);
    console.log("Participants:", participants);
  }, [conversations, selectedConversation, messages, participants]);

  // Removed fetchAllMessages - messages are now only fetched when a conversation is clicked
  // This prevents unnecessary API calls and infinite loops
  // updateAllTimers is called explicitly when needed (after fetchMessages and fetchConversations)

  useEffect(() => {
    const fetchData = async () => {
      await fetchConversations();
      if (conversationSid) {
        setSelectedConversation(conversationSid);
        await fetchMessages(conversationSid);
        await fetchParticipants(conversationSid);
      }
    };
    fetchData();
  }, [conversationSid, session]);

  useEffect(() => {
    if (!loading) {
      updateAllConversationDetails();
    }
  }, [conversations, messages, loading]);

  useEffect(() => {
    if (selectedConversation) {
      const interval = setInterval(() => {
        updateTimeRemaining(selectedConversation);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation]);

  // Fetch read-status timestamps once on mount (cheap DB-only lookup, no Twilio).
  // Per-conversation unread state is computed locally from this + dateUpdated.
  const fetchReadStatus = useCallback(async () => {
    if (!session || !user?.id) return;
    try {
      const res = await fetchWithAuth(
        `${API_URL}/whatsapp/read-status?userId=${encodeURIComponent(user.id)}`,
        { method: "GET" },
        session,
      );
      if (res.ok) {
        const data = await res.json();
        setReadStatus(data || {});
      }
    } catch (err) {
      console.error("Error fetching WhatsApp read status", err);
    }
  }, [session, user?.id]);

  useEffect(() => {
    fetchReadStatus();
  }, [fetchReadStatus]);

  // Mark conversation as read when opened
  useEffect(() => {
    if (!selectedConversation || !session || !user?.id) return;
    const markRead = async () => {
      try {
        await fetchWithAuth(
          `${API_URL}/whatsapp/conversations/${selectedConversation}/mark-read`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userId: user.id }),
          },
          session,
        );
        setReadStatus((prev) => ({
          ...prev,
          [selectedConversation]: new Date().toISOString(),
        }));
        window.dispatchEvent(new CustomEvent("whatsapp:unread-changed"));
      } catch (err) {
        console.error("Error marking WhatsApp conversation as read", err);
      }
    };
    markRead();
  }, [selectedConversation, session, user?.id]);

  const unreadCounts = useMemo(() => {
    const result: { [sid: string]: number } = {};
    conversations.forEach((conv) => {
      const lastReadIso = readStatus[conv.sid];
      if (!lastReadIso) {
        // No read row yet — treat as fully read until first poll initializes it.
        result[conv.sid] = 0;
        return;
      }
      const lastRead = new Date(lastReadIso).getTime();
      const updated = conv.dateUpdated
        ? new Date(conv.dateUpdated).getTime()
        : 0;
      result[conv.sid] = updated > lastRead ? 1 : 0;
    });
    return result;
  }, [conversations, readStatus]);

  const handleMarkUnread = async (fromDateIso?: string) => {
    if (!selectedConversation || !session || !user?.id) return;
    try {
      const res = await fetchWithAuth(
        `${API_URL}/whatsapp/conversations/${selectedConversation}/mark-unread`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: user.id, fromDateIso }),
        },
        session,
      );
      if (!res.ok) throw new Error("Markeren mislukt");
      toast.success("Gemarkeerd als ongelezen");
      if (!fromDateIso) {
        // Whole-conversation mark-unread: deselect so the badge is visible.
        setSelectedConversation(null);
      }
      fetchReadStatus();
      window.dispatchEvent(new CustomEvent("whatsapp:unread-changed"));
    } catch (err) {
      console.error("Error marking conversation unread", err);
      toast.error("Markeren als ongelezen mislukt");
    }
  };

  useEffect(() => {
    if (showConversationDrawer) {
      document.body.classList.add("overflow-hidden"); // Disable scrolling with Tailwind
    } else {
      document.body.classList.remove("overflow-hidden"); // Re-enable scrolling
    }
    return () => {
      document.body.classList.remove("overflow-hidden"); // Cleanup on unmount or drawer close
    };
  }, [showConversationDrawer]);

  // Lock body scroll while the conversations page is mounted, so the layout
  // stays fixed (WhatsApp-style) and only the inner panes scroll.
  useEffect(() => {
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, []);

  const calculateTimeRemaining = (lastMessageDate: Date) => {
    const now = new Date();
    const timeDiff =
      24 * 60 * 60 * 1000 - (now.getTime() - lastMessageDate.getTime());

    if (timeDiff <= 0) {
      return "00:00:00";
    }

    const hours = Math.floor(timeDiff / (60 * 60 * 1000));
    const minutes = Math.floor((timeDiff % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((timeDiff % (60 * 1000)) / 1000);

    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const getParsedAttributes = (attributes: any) => {
    try {
      return JSON.parse(attributes || "{}");
    } catch {
      return {}; // Fallback in case of parsing error
    }
  };

  const updateAllTimers = useCallback(
    debounce(() => {
      const newTimers: { [key: string]: string } = {};
      const newLastMessageDates: { [key: string]: string } = {};
      const newLastMessagePreviews: { [key: string]: string } = {};

      const isInbound = (m: any) =>
        typeof m.author === "string" &&
        (m.author.startsWith("whatsapp:") || /^\+\d+/.test(m.author));

      conversations.forEach((conversation) => {
        const conversationMessages = messages[conversation.sid] || [];
        if (conversationMessages.length > 0) {
          const lastMessage =
            conversationMessages[conversationMessages.length - 1];
          const lastInbound = [...conversationMessages]
            .filter(isInbound)
            .sort(
              (a, b) =>
                new Date(b.dateCreated).getTime() -
                new Date(a.dateCreated).getTime(),
            )[0];
          newLastMessageDates[conversation.sid] = lastMessage.dateCreated;
          // 24h template rule timer is based on the *customer's* last reply.
          newTimers[conversation.sid] = lastInbound
            ? calculateTimeRemaining(new Date(lastInbound.dateCreated))
            : "00:00:00";
          newLastMessagePreviews[conversation.sid] = truncateMessage(
            lastMessage.body,
          );
        } else {
          newLastMessageDates[conversation.sid] = "No messages";
          newTimers[conversation.sid] = "";
          newLastMessagePreviews[conversation.sid] = "No messages";
        }
      });

      setLastMessageDates(newLastMessageDates);
      setTimers(newTimers);
      setLastMessagePreviews(newLastMessagePreviews);
    }, 1000),
    [conversations, messages],
  );

  const updateTimeRemaining = (conversationSid: string) => {
    const conversation = conversations.find((c) => c.sid === conversationSid);
    if (!conversation) return;

    const conversationMessages = messages[conversationSid] || [];

    // Twilio/Meta rule: a free-form message can only be sent within 24h of the
    // *customer's* last inbound message. Outbound messages don't reset the window.
    const isInbound = (m: any) =>
      typeof m.author === "string" &&
      (m.author.startsWith("whatsapp:") || /^\+\d+/.test(m.author));

    const lastInboundDate = conversationMessages
      .filter(isInbound)
      .map((m: any) => new Date(m.dateCreated))
      .sort((a, b) => b.getTime() - a.getTime())[0];

    if (!lastInboundDate) {
      // No inbound message → can only send a template (treat as expired window).
      setTimeRemaining("00:00:00");
      setShowDummyContent(true);
      return;
    }

    const now = new Date();
    const timeDiff =
      24 * 60 * 60 * 1000 - (now.getTime() - lastInboundDate.getTime());

    if (timeDiff <= 0) {
      setTimeRemaining("00:00:00");
      setShowDummyContent(true);
    } else {
      const hours = Math.floor(timeDiff / (60 * 60 * 1000));
      const minutes = Math.floor((timeDiff % (60 * 60 * 1000)) / (60 * 1000));
      const seconds = Math.floor((timeDiff % (60 * 1000)) / 1000);
      setTimeRemaining(
        `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`,
      );
      setShowDummyContent(false);
    }
  };

  // Ref to track previous conversation for instant scroll on conversation change
  const prevConversationRef = useRef<string | null>(null);

  useEffect(() => {
    if (messageEndRef.current && selectedConversation) {
      // Instant scroll when opening a new conversation
      const isNewConversation =
        prevConversationRef.current !== selectedConversation;

      messageEndRef.current.scrollIntoView({
        behavior: isNewConversation ? "instant" : "smooth",
      });

      prevConversationRef.current = selectedConversation;
    }
  }, [selectedConversation, messages[selectedConversation as string]?.length]); // Trigger on conversation change or message count change

  // Add this useMemo to parse attributes for all conversations
  const parsedConversations = useMemo(() => {
    return conversations.map((conversation) => ({
      ...conversation,
      parsedAttributes: getParsedAttributes(conversation.attributes),
    }));
  }, [conversations]);

  const filteredConversations = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return conversations.filter((conversation: any) => {
      const attributes = JSON.parse(conversation.attributes || "{}");
      const matchesStateFilter =
        filter === "all" || conversation.state === filter;
      const matchesBuildingFilter =
        buildingFilter === "all" ||
        (buildingFilter === "none" && !attributes.buildingId) ||
        attributes.buildingId === buildingFilter;

      const lastReadIso = readStatus[conversation.sid];
      const lastRead = lastReadIso ? new Date(lastReadIso).getTime() : 0;
      const updatedTime = conversation.dateUpdated
        ? new Date(conversation.dateUpdated).getTime()
        : 0;
      const hasUnread = lastReadIso ? updatedTime > lastRead : false;
      const matchesChip =
        chipFilter === "all"
          ? true
          : chipFilter === "unread"
            ? hasUnread
            : chipFilter === "groups"
              ? (attributes.participants || []).length > 1
              : true; // favorites: not implemented yet, treat as "all"

      let matchesSearch = true;
      if (q) {
        const participantText = (attributes.participants || [])
          .map((p: any) => `${p.fullName || ""} ${p.identity || ""}`)
          .join(" ")
          .toLowerCase();
        const lastBody = (
          conversation.lastMessage?.body || ""
        ).toLowerCase();
        const friendly = (conversation.friendlyName || "").toLowerCase();
        matchesSearch =
          participantText.includes(q) ||
          lastBody.includes(q) ||
          friendly.includes(q);
      }

      return (
        matchesStateFilter &&
        matchesBuildingFilter &&
        matchesChip &&
        matchesSearch
      );
    });
  }, [
    conversations,
    filter,
    buildingFilter,
    chipFilter,
    searchQuery,
    readStatus,
  ]);

  // Add a new function to group conversations
  const groupedConversations = useMemo(() => {
    if (groupBy === "none")
      return { "All Conversations": filteredConversations };

    const grouped = filteredConversations.reduce(
      (acc: { [key: string]: any[] }, conversation) => {
        const attributes = JSON.parse(conversation.attributes || "{}");
        const key = attributes[`${groupBy}Id`] || "None";

        if (!acc[key]) {
          acc[key] = [];
        }

        acc[key].push(conversation);
        return acc;
      },
      {},
    );

    // Sort conversations within each group by date
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort(
        (a, b) =>
          new Date(b.dateUpdated).getTime() - new Date(a.dateUpdated).getTime(),
      );
    });

    // Sort groups by their keys
    return Object.keys(grouped)
      .sort()
      .reduce(
        (acc, key) => {
          acc[key] = grouped[key];
          return acc;
        },
        {} as { [key: string]: any[] },
      );
  }, [filteredConversations, groupBy]);

  // Extract unique building IDs using memoized parsed attributes
  const uniqueBuildingIds = useMemo(() => {
    const buildingIds = new Set<string>();
    parsedConversations.forEach((conversation) => {
      if (conversation.parsedAttributes.buildingId) {
        buildingIds.add(conversation.parsedAttributes.buildingId);
      }
    });
    return Array.from(buildingIds);
  }, [parsedConversations]);

  // Define fetchMessages first so it can be used in fetchConversations
  const fetchMessages = useCallback(
    async (conversationSid: string) => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/whatsapp/twilio-messages/${conversationSid}`,
          { method: "GET" },
          session,
        );
        if (!response.ok) throw new Error("Failed to fetch messages");
        const data = await response.json();
        setMessages((prev) => ({ ...prev, [conversationSid]: data }));

        // Update timers and previews immediately
        updateAllTimers();
      } catch (error) {
        console.error("Error fetching messages:", error);
      }
    },
    [session],
  );

  const fetchConversations = useCallback(
    async (pageToken?: string) => {
      try {
        const url = pageToken
          ? `${API_URL}/whatsapp/twilio-conversations?pageSize=15&pageToken=${pageToken}`
          : `${API_URL}/whatsapp/twilio-conversations?pageSize=15`;

        const response = await fetchWithAuth(url, { method: "GET" }, session);
        if (!response.ok) throw new Error("Failed to fetch conversations");
        const data = await response.json();

        if (pageToken) {
          setConversations((prev) => [...prev, ...data.conversations]);
        } else {
          // BE inlines `lastMessage` per conversation, so previews are ready
          // without N extra `fetchMessages` round-trips.
          setConversations(data.conversations);
          setLoading(false);
        }

        setNextPageToken(data.nextPageToken);
        setLoadingMore(false);
        // Don't call updateAllTimers here - let it be called after messages are fetched
      } catch (error) {
        console.error("Error fetching conversations:", error);
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [session, fetchMessages],
  );

  const loadMoreConversations = useCallback(async () => {
    if (!nextPageToken || loadingMore) return;
    setLoadingMore(true);
    await fetchConversations(nextPageToken);
  }, [nextPageToken, loadingMore, fetchConversations]);

  const sendMessage = async () => {
    if (!newMessage.trim() && !selectedTemplate) return;

    try {
      let response;
      if (selectedTemplate) {
        // Send template message
        response = await fetchWithAuth(
          `${API_URL}/whatsapp/send-template`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              to: participants[selectedConversation as string][0]
                .messagingBinding.address,
              templateSid: selectedTemplate.sid,
              templateContent: selectedTemplate.body,
              templateParameters: {},
            }),
          },
          session,
        );
      } else {
        // Send regular message
        response = await fetchWithAuth(
          `${API_URL}/whatsapp/conversations/${selectedConversation}/messages`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              content: newMessage,
              author: "system",
            }),
          },
          session,
        );
      }

      if (!response.ok) throw new Error("Failed to send message");

      const sentMessage = await response.json();

      // Update the local messages state
      setMessages((prev) => ({
        ...prev,
        [selectedConversation as string]: [
          ...(prev[selectedConversation as string] || []),
          sentMessage,
        ],
      }));

      // Update the time remaining
      updateTimeRemaining(selectedConversation as string);

      setNewMessage("");
      setSelectedTemplate(null);
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    }
  };

  const fetchParticipants = useCallback(
    async (conversationSid: string) => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/whatsapp/conversations/${conversationSid}/participants`,
          { method: "GET" },
          session,
        );
        if (!response.ok) throw new Error("Failed to fetch participants");
        const data = await response.json();
        setParticipants((prev) => ({ ...prev, [conversationSid]: data }));
      } catch (error) {
        console.error("Error fetching participants:", error);
      }
    },
    [session],
  );

  // Handle initial phone parameter from URL (deep linking from incasso, etc.)
  // NOTE: This useEffect must be placed after fetchMessages and fetchParticipants are declared
  useEffect(() => {
    if (!loading && initialPhone && !initialPhoneHandled && conversations.length > 0) {
      setInitialPhoneHandled(true);

      // Normalize phone number for comparison
      const normalizePhone = (phone: string) => {
        let normalized = phone.replace(/\s+/g, "").replace(/[^0-9+]/g, "");
        if (normalized.startsWith("0")) {
          normalized = "+32" + normalized.substring(1);
        }
        return normalized;
      };

      const normalizedInitialPhone = normalizePhone(initialPhone);

      // Search for existing conversation with this phone number
      const existingConversation = conversations.find((conv) => {
        try {
          const attrs = JSON.parse(conv.attributes || "{}");
          const convParticipants = attrs.participants || [];
          return convParticipants.some((p: any) => {
            const participantPhone = p.phone || p.phoneNumber || p.address;
            if (participantPhone) {
              return normalizePhone(participantPhone) === normalizedInitialPhone;
            }
            return false;
          });
        } catch {
          return false;
        }
      });

      if (existingConversation) {
        // Found existing conversation - select it
        setSelectedConversation(existingConversation.sid);
        fetchMessages(existingConversation.sid);
        fetchParticipants(existingConversation.sid);
        toast.info(`Bestaand gesprek gevonden met ${initialName || initialPhone}`);
      } else {
        // No existing conversation - open the add modal with prefilled info
        setInitialParticipantForModal({
          phone: initialPhone,
          contactId: initialContactId || undefined,
          companyId: initialCompanyId || undefined,
          name: initialName || undefined,
        });
        setShowAddConversationModal(true);
        toast.info(`Geen bestaand gesprek gevonden. Start een nieuw gesprek met ${initialName || initialPhone}.`);
      }

      // Clear the URL params after handling
      setSearchParams({});
    }
  }, [loading, initialPhone, initialPhoneHandled, conversations, fetchMessages, fetchParticipants, initialName, initialContactId, initialCompanyId, setSearchParams]);

  const updateConversationDetails = async () => {
    if (!selectedConversation) return;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/whatsapp/conversations/${selectedConversation}/details`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ friendlyName: newFriendlyName }),
        },
        session,
      );
      if (!response.ok)
        throw new Error("Failed to update conversation details");
      fetchConversations();
      setShowEditModal(false);
      toast.success("Conversation updated successfully");
    } catch (error) {
      console.error("Error updating conversation details:", error);
      toast.error("Failed to update conversation");
    }
  };

  const handleUpdateMetadata = () => {
    if (!selectedConversation) {
      toast.error("No conversation selected");
      return;
    }
    try {
      const metadata = JSON.parse(newMetadata);
      updateConversationMetadata(selectedConversation, metadata);
    } catch (error) {
      console.error("Error parsing metadata:", error);
      toast.error("Invalid metadata format");
    }
  };

  const updateConversationMetadata = async (
    conversationSid: string,
    metadata: any,
  ) => {
    try {
      await fetchWithAuth(
        `${API_URL}/whatsapp/conversations/${conversationSid}/metadata`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(metadata),
        },
        session,
      );
      fetchConversations();
      setShowEditModal(false);
      toast.success("Metadata updated successfully");
    } catch (error) {
      console.error("Error updating conversation metadata:", error);
      toast.error("Failed to update metadata");
    }
  };

  const addParticipant = async () => {
    if (!selectedConversation) return;
    try {
      const response = await fetchWithAuth(
        `${API_URL}/whatsapp/conversations/${selectedConversation}/participants`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            identity: newParticipantIdentity,
            address: newParticipantAddress,
            attributes: newParticipantMetadata,
          }),
        },
        session,
      );
      if (!response.ok) throw new Error("Failed to add participant");
      fetchParticipants(selectedConversation);
      setShowAddParticipantModal(false);
      setNewParticipantIdentity("");
      setNewParticipantAddress("");
      setNewParticipantMetadata("");
      toast.success("Participant added successfully");
    } catch (error) {
      console.error("Error adding participant:", error);
      toast.error("Failed to add participant");
    }
  };

  const updateParticipant = async () => {
    if (!selectedConversation || !editingParticipant) return;
    try {
      const updatedAttributes = JSON.parse(newParticipantMetadata);

      const response = await fetchWithAuth(
        `${API_URL}/whatsapp/conversations/${selectedConversation}/participants/${editingParticipant.sid}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attributes: JSON.stringify(updatedAttributes),
          }),
        },
        session,
      );
      if (!response.ok) throw new Error("Failed to update participant");
      fetchParticipants(selectedConversation);
      setShowEditParticipantModal(false);
      setEditingParticipant(null);
      setNewParticipantMetadata("");
      toast.success("Participant updated successfully");
    } catch (error) {
      console.error("Error updating participant:", error);
      toast.error("Failed to update participant");
    }
  };

  const sendTemplateMessage = useCallback(
    async (
      template: any,
      templateParams: Record<string, Record<string, string>>,
    ) => {
      console.log("Trying to send template message:", template, templateParams);
      if (!selectedConversation) return;

      console.log("With template params");
      console.log(templateParams);

      console.log(
        "Sending template message to participants:",
        participants[selectedConversation],
      );
      const conversationParticipants = participants[selectedConversation];

      if (!conversationParticipants) {
        return toast.error("No participants found for this conversation");
      }

      try {
        const responses = await Promise.all(
          conversationParticipants.map(async (participant) => {
            console.log("Sending template to participant:", participant);

            return fetchWithAuth(
              `${API_URL}/whatsapp/send-template`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  to: participant.messagingBinding.address,
                  templateSid: template.sid,
                  templateContent:
                    template &&
                    template.types["twilio/quick-reply"] &&
                    template.types["twilio/quick-reply"].body,
                  templateParameters: templateParams[participant.sid] || {},
                }),
              },
              session,
            );
          }),
        );

        const sentMessages = await Promise.all(
          responses.filter((r) => r !== null).map((r: any) => r.json()),
        );

        // Update the local messages state
        setMessages((prev) => ({
          ...prev,
          [selectedConversation]: [
            ...(prev[selectedConversation] || []),
            ...sentMessages,
          ],
        }));

        // Update the time remaining
        updateTimeRemaining(selectedConversation);

        toast.success("Template messages sent successfully");
      } catch (error) {
        console.error("Error sending template messages:", error);
        toast.error("Failed to send template messages");
      }
    },
    [selectedConversation, participants, session, updateTimeRemaining],
  );

  const handleDeleteConversation = async () => {
    if (!selectedConversation) return;

    try {
      await fetchWithAuth(
        `${API_URL}/whatsapp/conversations/${selectedConversation}`,
        {
          method: "DELETE",
        },
        session,
      );

      setConversations(
        conversations.filter((c) => c.sid !== selectedConversation),
      );
      setSelectedConversation(null);
      setShowDeleteModal(false);
      toast.success("Conversation deleted successfully");
    } catch (error) {
      console.error("Error deleting conversation:", error);
      toast.error("Failed to delete conversation");
    }
  };

  const handleSelectTemplate = useCallback(
    (template: any, params: Record<string, Record<string, string>>) => {
      console.log("Selected template:", template);
      console.log("With params:", params);
      setSelectedTemplate(template);
      setShowTemplateModal(false);
      sendTemplateMessage(template, params);
    },
    [sendTemplateMessage],
  );

  const handleAddConversation = async (
    participants: any[],
    metadata: any,
    template: any,
    templateParams: TemplateParam[],
  ) => {
    try {
      console.log("Creating conversation with participants:", participants);
      console.log("With template and params:", template, templateParams);

      // Create individual conversations
      const newConversations = await Promise.all(
        participants.map(async (participant) => {
          // Iterate through the keys and store them in the format Twilio expects like { 1: 'value', 2: 'value', etc... }
          // If the key has a global value use that, otherwise use the personal value

          const participantTemplateParams = Object.fromEntries(
            templateParams.map((param) => {
              const value = param.isGlobal
                ? param.globalValue
                : param.personalValues[participant.id]; // cause the personalValues key is the resource.id of the participant
              return [param.key, value];
            }),
          );

          const response = await fetchWithAuth(
            `${API_URL}/whatsapp/conversations`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                participant,
                metadata,
                template,
                templateParams: participantTemplateParams,
              }),
            },
            session,
          );

          if (!response.ok)
            throw new Error(
              `Failed to create conversation for ${participant.sid}`,
            );

          return response.json();
        }),
      );

      setConversations([...conversations, ...newConversations]);
      toast.success(
        `${newConversations.length} individual conversations created successfully`,
      );
    } catch (error) {
      console.error("Error creating conversation(s):", error);
      toast.error("Failed to create conversation(s)");
    }
  };

  const truncateMessage = (
    message: string,
    maxLines = 3,
    charsPerLine = 30,
  ) => {
    const words = (message && message.split(" ")) || [];
    let lines = 0;
    let truncated = "";

    for (let word of words) {
      if ((truncated.length + word.length + 1) / charsPerLine > lines + 1) {
        lines++;
        if (lines >= maxLines) {
          truncated += "...";
          break;
        }
        truncated += "\n";
      }
      truncated += (truncated ? " " : "") + word;
    }

    return truncated;
  };

  const updateAllConversationDetails = () => {
    conversations.forEach((conversation) => {
      const conversationMessages = messages[conversation.sid] || [];
      if (conversationMessages.length > 0) {
        const lastMessage =
          conversationMessages[conversationMessages.length - 1];
        setLastMessageDates((prev) => ({
          ...prev,
          [conversation.sid]: lastMessage.dateCreated,
        }));
        setTimers((prev) => ({
          ...prev,
          [conversation.sid]: calculateTimeRemaining(
            new Date(lastMessage.dateCreated),
          ),
        }));
        setLastMessagePreviews((prev) => ({
          ...prev,
          [conversation.sid]: truncateMessage(lastMessage.body),
        }));
      }
    });
  };

  const updateConversationState = async (
    conversationSid: string,
    state: "active" | "inactive" | "closed",
  ) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/whatsapp/conversations/${conversationSid}/state`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ state }),
        },
        session,
      );
      if (!response.ok) throw new Error("Failed to update conversation state");
      fetchConversations();
      toast.success("Conversation state updated successfully");
    } catch (error) {
      console.error("Error updating conversation state:", error);
      toast.error("Failed to update conversation state");
    }
  };

  const handleResourceSelect = async (resource: Resource) => {
    setSelectedResource(resource);
    if (selectedConversation) {
      const metadata = conversations.find(
        (c) => c.sid === selectedConversation,
      )?.attributes;
      const updatedMetadata = {
        ...JSON.parse(metadata || "{}"),
        [`${resource.type}Id`]: resource.resourceId,
      };
      await updateConversationMetadata(selectedConversation, updatedMetadata);
      fetchConversations(); // Refresh the conversations list
    }
  };

  return (
    <>
      <div
        className="fixed right-0 bottom-0 left-0 flex flex-col overflow-hidden"
        style={{ top: "var(--header-height, 70px)" }}
      >
        <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(0,1fr)] overflow-hidden sm:grid-cols-[380px_1fr]">
          <aside className="hidden h-full min-h-0 flex-col overflow-hidden border-r border-neutral-primary/10 sm:flex">
            {/* WhatsApp-style sidebar header */}
            <div className="flex flex-shrink-0 flex-col gap-3 border-b border-neutral-primary/10 bg-white px-3 pt-4 pb-3">
              <div className="flex items-center justify-between">
                <h2 className="text-neutral-primary text-xl font-bold">
                  Chats
                </h2>
                <div className="flex items-center gap-1">
                  <Tooltip content="Twilio Console">
                    <a
                      href="https://console.twilio.com/us1/monitor/logs/sms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-neutral-secondary-mid hover:text-moss-primary inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-neutral-100"
                      aria-label="Twilio Console"
                    >
                      <ArrowSquareOut size={18} weight="bold" />
                    </a>
                  </Tooltip>
                  <Tooltip content="Twilio templates">
                    <a
                      href="https://console.twilio.com/us1/develop/sms/content-template-builder"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-neutral-secondary-mid hover:text-moss-primary inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-neutral-100"
                      aria-label="Twilio templates"
                    >
                      <ChatTeardropText size={18} weight="duotone" />
                    </a>
                  </Tooltip>
                  <Tooltip content="Nieuwe conversatie">
                    <button
                      type="button"
                      onClick={() => setShowAddConversationModal(true)}
                      className="text-neutral-secondary-mid hover:text-moss-primary inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-neutral-100"
                      aria-label="Nieuwe conversatie"
                    >
                      <NotePencil size={20} weight="bold" />
                    </button>
                  </Tooltip>
                  <Dropdown placement="bottom-end">
                    <DropdownTrigger>
                      <button
                        type="button"
                        className="text-neutral-secondary-mid hover:text-neutral-primary inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-neutral-100"
                        aria-label="Meer opties"
                      >
                        <DotsThreeVertical size={20} weight="bold" />
                      </button>
                    </DropdownTrigger>
                    <DropdownMenu aria-label="Sidebar opties">
                      <DropdownItem
                        key="status-header"
                        isReadOnly
                        className="text-neutral-secondary-mid pointer-events-none cursor-default text-[10px] uppercase"
                      >
                        Status
                      </DropdownItem>
                      <DropdownItem
                        key="status-all"
                        onPress={() => setFilter("all")}
                      >
                        {filter === "all" ? "✓ " : ""}Alle statussen
                      </DropdownItem>
                      <DropdownItem
                        key="status-active"
                        onPress={() => setFilter("active")}
                      >
                        {filter === "active" ? "✓ " : ""}Actief
                      </DropdownItem>
                      <DropdownItem
                        key="status-inactive"
                        onPress={() => setFilter("inactive")}
                      >
                        {filter === "inactive" ? "✓ " : ""}Inactief
                      </DropdownItem>
                      <DropdownItem
                        key="status-closed"
                        onPress={() => setFilter("closed")}
                      >
                        {filter === "closed" ? "✓ " : ""}Gearchiveerd
                      </DropdownItem>
                      <DropdownItem
                        key="group-header"
                        isReadOnly
                        className="text-neutral-secondary-mid pointer-events-none cursor-default text-[10px] uppercase"
                      >
                        Groeperen
                      </DropdownItem>
                      <DropdownItem
                        key="group-none"
                        onPress={() => setGroupBy("none")}
                      >
                        {groupBy === "none" ? "✓ " : ""}Geen groepering
                      </DropdownItem>
                      <DropdownItem
                        key="group-building"
                        onPress={() => setGroupBy("building")}
                      >
                        {groupBy === "building" ? "✓ " : ""}Per gebouw
                      </DropdownItem>
                      <DropdownItem
                        key="group-unit"
                        onPress={() => setGroupBy("unit")}
                      >
                        {groupBy === "unit" ? "✓ " : ""}Per unit
                      </DropdownItem>
                      <DropdownItem
                        key="group-contact"
                        onPress={() => setGroupBy("contact")}
                      >
                        {groupBy === "contact" ? "✓ " : ""}Per contact
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>

              {/* Search */}
              <div className="relative">
                <MagnifyingGlass
                  size={16}
                  className="text-neutral-secondary-mid pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Zoek in WhatsApp gesprekken..."
                  className="text-neutral-primary placeholder:text-neutral-secondary-mid focus:ring-moss-primary/30 w-full rounded-full bg-neutral-100 py-2 pr-3 pl-9 text-sm focus:outline-none focus:ring-2"
                />
              </div>

              {/* Filter chips */}
              <div className="flex items-center gap-2 overflow-x-auto">
                {(
                  [
                    { key: "all", label: "Alle" },
                    { key: "unread", label: "Ongelezen" },
                    { key: "groups", label: "Groepen" },
                  ] as const
                ).map((chip) => (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => setChipFilter(chip.key)}
                    className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      chipFilter === chip.key
                        ? "bg-moss-primary/15 text-moss-primary"
                        : "bg-neutral-100 text-neutral-secondary-mid hover:bg-neutral-200"
                    }`}
                  >
                    {chip.label}
                  </button>
                ))}
                {uniqueBuildingIds.length > 0 && (
                  <Dropdown placement="bottom-start">
                    <DropdownTrigger>
                      <button
                        type="button"
                        className={`flex-shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                          buildingFilter !== "all"
                            ? "bg-moss-primary/15 text-moss-primary"
                            : "bg-neutral-100 text-neutral-secondary-mid hover:bg-neutral-200"
                        }`}
                      >
                        {buildingFilter === "all"
                          ? "Gebouw"
                          : buildingFilter === "none"
                            ? "Geen gebouw"
                            : buildingFilter}
                        <CaretDown size={10} weight="bold" />
                      </button>
                    </DropdownTrigger>
                    <DropdownMenu
                      aria-label="Building filter"
                      onAction={(key) => setBuildingFilter(key as string)}
                    >
                      <>
                        <DropdownItem key="all">Alle gebouwen</DropdownItem>
                        {uniqueBuildingIds.map((buildingId) => (
                          <DropdownItem key={buildingId}>
                            {buildingId}
                          </DropdownItem>
                        ))}
                        <DropdownItem key="none">Geen gebouw</DropdownItem>
                      </>
                    </DropdownMenu>
                  </Dropdown>
                )}
              </div>
            </div>

            {/* Conversation list */}
            {groupBy === "none" ? (
              <ConversationList
                conversations={filteredConversations}
                selectedConversation={selectedConversation}
                setSelectedConversation={setSelectedConversation}
                fetchMessages={fetchMessages}
                fetchParticipants={fetchParticipants}
                lastMessagePreviews={lastMessagePreviews}
                lastMessageDates={lastMessageDates}
                timers={timers}
                messages={messages}
                onLoadMore={loadMoreConversations}
                hasMore={!!nextPageToken}
                loadingMore={loadingMore}
                unreadCounts={unreadCounts}
                isLoading={loading && conversations.length === 0}
                emptyMessage={
                  searchQuery
                    ? `Geen resultaten voor "${searchQuery}"`
                    : chipFilter === "unread"
                      ? "Geen ongelezen gesprekken"
                      : chipFilter === "groups"
                        ? "Geen groepsgesprekken"
                        : "Geen gesprekken gevonden"
                }
              />
            ) : (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <Accordion>
                  {Object.entries(groupedConversations).map(
                    ([group, conversations]) => (
                      <AccordionItem key={group} title={group}>
                        <ConversationList
                          conversations={conversations}
                          selectedConversation={selectedConversation}
                          setSelectedConversation={setSelectedConversation}
                          fetchMessages={fetchMessages}
                          fetchParticipants={fetchParticipants}
                          lastMessagePreviews={lastMessagePreviews}
                          lastMessageDates={lastMessageDates}
                          timers={timers}
                          messages={messages}
                          onLoadMore={loadMoreConversations}
                          hasMore={!!nextPageToken}
                          loadingMore={loadingMore}
                          unreadCounts={unreadCounts}
                          isLoading={loading && conversations.length === 0}
                          emptyMessage={
                            searchQuery
                              ? `Geen resultaten voor "${searchQuery}"`
                              : "Geen gesprekken in deze groep"
                          }
                        />
                      </AccordionItem>
                    ),
                  )}
                </Accordion>
              </div>
            )}
          </aside>
          {selectedConversation && (
            <div className="grid h-full min-h-0 grid-rows-[auto_auto_minmax(0,1fr)_auto] overflow-hidden">
              <header className="border-neutral-primary/10 flex items-center justify-between border-b bg-white px-4 py-3">
                <div className="flex min-w-0 items-center gap-3">
                  {/* Avatar */}
                  <div className="bg-neutral-primary/10 text-neutral-primary flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full">
                    <span className="text-sm font-semibold">
                      {(() => {
                        const conv = conversations.find(
                          (c) => c.sid === selectedConversation,
                        );
                        const attrs = conv
                          ? JSON.parse(conv.attributes || "{}")
                          : {};
                        const participants = attrs.participants || [];
                        const name = participants
                          .map((p: any) => p.fullName)
                          .join(", ");
                        return name ? name.substring(0, 2).toUpperCase() : "??";
                      })()}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h2 className="text-neutral-primary truncate text-base font-semibold">
                      {(() => {
                        const conv = conversations.find(
                          (c) => c.sid === selectedConversation,
                        );
                        const attrs = conv
                          ? JSON.parse(conv.attributes || "{}")
                          : {};
                        const participants = attrs.participants || [];
                        return (
                          participants.map((p: any) => p.fullName).join(", ") ||
                          conv?.friendlyName ||
                          "Onbekend"
                        );
                      })()}
                    </h2>
                    {conversations.find((c) => c.sid === selectedConversation)
                      ?.state === "closed" && (
                      <span className="rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-800">
                        Gearchiveerd
                      </span>
                    )}
                    {conversations.find((c) => c.sid === selectedConversation)
                      ?.state === "inactive" && (
                      <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800">
                        Inactief
                      </span>
                    )}
                  </div>
                  {/* Quick contact link to first participant */}
                  {(() => {
                    const firstParticipant =
                      participants[selectedConversation]?.[0];
                    if (!firstParticipant) return null;
                    const meta = JSON.parse(
                      firstParticipant.attributes || "{}",
                    );
                    const contactId = meta.contactId;
                    if (!contactId) return null;
                    return (
                      <Tooltip content="Open contact">
                        <Link
                          to={`/contact/${contactId}`}
                          className="text-neutral-secondary-mid hover:text-moss-primary flex-shrink-0"
                        >
                          <ArrowUpRight size={18} weight="bold" />
                        </Link>
                      </Tooltip>
                    );
                  })()}
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  <Dropdown placement="bottom-end">
                    <DropdownTrigger>
                      <button
                        className="text-neutral-secondary-mid hover:text-neutral-primary hover:bg-neutral-100 rounded-full p-2"
                        aria-label="Beheer"
                      >
                        <DotsThreeVertical size={20} weight="bold" />
                      </button>
                    </DropdownTrigger>
                    <DropdownMenu aria-label="Gespreksacties">
                      <DropdownItem
                        key="mark-unread"
                        startContent={<EnvelopeOpen size={16} weight="duotone" />}
                        onPress={() => handleMarkUnread()}
                      >
                        Markeer als ongelezen
                      </DropdownItem>
                      <DropdownItem
                        key="link-resource"
                        startContent={<ArrowUpRight size={16} weight="duotone" />}
                        onPress={() => setShowEditModal(true)}
                      >
                        Bewerken / metadata
                      </DropdownItem>
                      {conversations.find((c) => c.sid === selectedConversation)
                        ?.state === "inactive" ? (
                        <DropdownItem
                          key="reactivate"
                          startContent={<Archive size={16} weight="duotone" />}
                          onPress={() =>
                            updateConversationState(selectedConversation, "active")
                          }
                        >
                          Heractiveren
                        </DropdownItem>
                      ) : conversations.find((c) => c.sid === selectedConversation)
                          ?.state === "closed" ? null : (
                        <>
                          <DropdownItem
                            key="inactive"
                            startContent={<Archive size={16} weight="duotone" />}
                            onPress={() =>
                              updateConversationState(
                                selectedConversation,
                                "inactive",
                              )
                            }
                          >
                            Inactief maken
                          </DropdownItem>
                          <DropdownItem
                            key="archive"
                            startContent={<Archive size={16} weight="duotone" />}
                            onPress={() => {
                              if (
                                confirm(
                                  "Let op: Gearchiveerde conversaties kunnen niet meer heropend worden. Er wordt een nieuwe conversatie aangemaakt bij toekomstig contact. Doorgaan?",
                                )
                              ) {
                                updateConversationState(
                                  selectedConversation,
                                  "closed",
                                );
                              }
                            }}
                          >
                            Archiveren (permanent)
                          </DropdownItem>
                        </>
                      )}
                      <DropdownItem
                        key="delete"
                        className="text-danger"
                        color="danger"
                        startContent={<Trash size={16} weight="duotone" />}
                        onPress={() => setShowDeleteModal(true)}
                      >
                        Verwijderen
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </header>
              {/* Resource link bar - subtle, below header */}
              <div className="border-neutral-primary/10 flex items-center gap-2 border-b bg-white px-4 py-2">
                <SearchComponent
                  resourceFilter={[
                    ResourceType.BUILDING,
                    ResourceType.UNIT,
                    ResourceType.CONTACT,
                  ]}
                  onSelect={handleResourceSelect}
                  classNames="flex-1"
                  label="Link gebouw, unit, contact aan conversatie"
                />
              </div>
              {/* Messages container - WhatsApp-style background */}
              <div
                className="flex flex-col overflow-y-auto"
                style={{ backgroundColor: "#efeae2" }}
              >
                <div className="flex flex-1 flex-col gap-1 overflow-x-hidden px-6 py-4">
                  {messages[selectedConversation]?.map((message) => {
                    const isOutbound =
                      message.author === "system" ||
                      (typeof message.author === "string" &&
                        !message.author.startsWith("whatsapp:") &&
                        !/^\+\d+/.test(message.author));
                    return (
                    <div
                      key={message.sid}
                      className={`group mb-1 flex ${isOutbound ? "justify-end" : "justify-start"}`}
                    >
                      <div className="relative max-w-[65%]">
                        <div
                          className={`relative overflow-hidden rounded-lg px-3 py-2 pr-8 shadow-sm ${
                            isOutbound
                              ? "text-neutral-primary rounded-tr-none"
                              : "text-neutral-primary rounded-tl-none bg-white"
                          }`}
                          style={
                            isOutbound ? { backgroundColor: "#d9fdd3" } : undefined
                          }
                        >
                          <MessageActionsMenu
                            tone={isOutbound ? "outbound" : "inbound"}
                            onMarkUnread={() =>
                              handleMarkUnread(message.dateCreated)
                            }
                          />
                          {message.body && (
                            <p className="text-sm break-words">
                              {message.body}
                            </p>
                          )}
                          {message.media &&
                            message.media.length > 0 &&
                            message.media.map((mediaItem: any, index) => (
                              <div key={index} className="mt-2">
                                {mediaItem.content_type?.startsWith(
                                  "image/",
                                ) ? (
                                  <img
                                    src={mediaItem.mediaUrl}
                                    alt={mediaItem.filename || "Attached image"}
                                    className="max-h-[300px] cursor-pointer rounded"
                                    onClick={() =>
                                      setFullscreenImage(mediaItem.mediaUrl)
                                    }
                                  />
                                ) : mediaItem.content_type?.startsWith(
                                    "video/",
                                  ) ? (
                                  <video
                                    src={mediaItem.mediaUrl}
                                    controls
                                    className="max-h-[300px] rounded"
                                  >
                                    Your browser does not support the video tag.
                                  </video>
                                ) : (
                                  <a
                                    href={mediaItem.mediaUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-sm underline"
                                  >
                                    {mediaItem.filename || "View attachment"}
                                  </a>
                                )}
                              </div>
                            ))}
                          <div className="mt-1 flex items-center justify-end gap-1 text-[11px] text-neutral-secondary-mid">
                            <span>
                              {(() => {
                                const d = new Date(message.dateCreated);
                                return d.toLocaleTimeString("nl-BE", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                });
                              })()}
                            </span>
                            {isOutbound && (
                              <svg
                                viewBox="0 0 16 15"
                                width="14"
                                height="13"
                                className="fill-current"
                              >
                                <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.483a.418.418 0 0 0 .036.54l1.32 1.267a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.892 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                  <div ref={messageEndRef} />
                </div>
              </div>
              <footer className="border-neutral-primary/10 relative flex items-start gap-4 border-t bg-white px-6 py-4">
                {showDummyContent && (
                  <div className="divide-neutral-primary/20 absolute inset-0 z-10 flex flex-col gap-4 divide-y bg-white/95 p-4 backdrop-blur-sm">
                    <header className="flex flex-col gap-0.5">
                      <div className="flex items-center gap-1 font-medium">
                        <ChatCircleSlash weight="duotone" size={18} />
                        <p>Deze chat is momenteel inactief</p>
                      </div>
                      <p className="text-sm">
                        Selecteer een template om het gesprek opnieuw op te
                        starten.
                      </p>
                    </header>
                    <ButtonNew
                      variant="primaryNeutral"
                      onClick={() => setShowTemplateModal(true)}
                      icon={<Plus weight="duotone" />}
                      label="Template selecteren"
                    />
                  </div>
                )}
                <div className="flex w-full gap-2">
                  <Textarea
                    placeholder="Typ een bericht..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    disabled={showDummyContent}
                    className="flex-1"
                  />
                  <div className="flex flex-col gap-2">
                    <Tooltip content="Tijd voordat deze chat op inactief zal worden geplaatst">
                      <div className="border-neutral-primary/10 flex items-center justify-center rounded-full border p-2 text-sm font-medium">
                        <Clock weight="duotone" size={16} />
                        <span className="ml-1">{timeRemaining}</span>
                      </div>
                    </Tooltip>
                    <ButtonNew
                      variant="primaryMoss"
                      onClick={() => setShowTemplateModal(true)}
                      icon={<Plus weight="duotone" />}
                      label="Template selecteren"
                    />
                    <ButtonNew
                      variant="primaryMoss"
                      onClick={sendMessage}
                      isDisabled={
                        (!newMessage.trim() && !selectedTemplate) ||
                        showDummyContent
                      }
                      icon={<PaperPlaneTilt weight="duotone" />}
                      label="Verstuur"
                    />
                  </div>
                </div>
              </footer>
            </div>
          )}
        </div>

        {/* Edit Conversation Modal */}
        {showEditModal && (
          <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
            <div className="flex w-full max-w-2xl flex-col gap-4 rounded-lg bg-white p-6">
              <h2 className="text-neutral-primary text-xl font-bold">
                Gesprek bewerken
              </h2>
              <Input
                value={newFriendlyName}
                onChange={(e) => setNewFriendlyName(e.target.value)}
                placeholder="Titel"
                classNames={{
                  input:
                    "text-neutral-primary placeholder:text-neutral-secondary-mid",
                  inputWrapper:
                    "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element! data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv! group-data-[focus=true]:shadow-form-element-fc!",
                }}
              />
              <Textarea
                value={newMetadata}
                onChange={(e) => setNewMetadata(e.target.value)}
                placeholder="Metadata (JSON)"
                rows={4}
                classNames={{
                  input:
                    "text-neutral-primary placeholder:text-neutral-secondary-mid",
                  inputWrapper:
                    "p-2 min-h-fit h-fit rounded-sm bg-white shadow-form-element! data-[hover=true]:bg-white group-data-[focus=true]:bg-white data-[hover=true]:shadow-form-element-hv! group-data-[focus=true]:shadow-form-element-fc!",
                }}
              />
              <div className="flex items-center justify-between gap-2">
                <ButtonNew
                  variant="secondaryNeutral"
                  onClick={() => setShowEditModal(false)}
                  icon={<X weight="duotone" />}
                  label="Sluiten"
                />
                <div className="flex items-center gap-2">
                  <ButtonNew
                    variant="primaryMoss"
                    onClick={updateConversationDetails}
                    icon={<Check weight="duotone" />}
                    label="Naam bewaren"
                  />
                  <ButtonNew
                    variant="primaryMoss"
                    onClick={handleUpdateMetadata}
                    icon={<Check weight="duotone" />}
                    label="Metadata bewaren"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add Participant Modal */}
        {showAddParticipantModal && (
          <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
            <div className="w-full max-w-md rounded-lg bg-white p-6">
              <h2 className="text-neutral-primary mb-4 text-xl font-bold">
                Add Participant
              </h2>
              <Input
                value={newParticipantIdentity}
                onChange={(e) => setNewParticipantIdentity(e.target.value)}
                placeholder="Participant Identity"
                className="mb-4"
              />
              <Input
                value={newParticipantAddress}
                onChange={(e) => setNewParticipantAddress(e.target.value)}
                placeholder="Participant Address"
                className="mb-4"
              />
              <Textarea
                value={newParticipantMetadata}
                onChange={(e) => setNewParticipantMetadata(e.target.value)}
                placeholder="Participant Metadata (JSON)"
                className="mb-4"
                rows={4}
              />
              <div className="flex justify-end gap-2">
                <Button onClick={() => setShowAddParticipantModal(false)}>
                  Cancel
                </Button>
                <Button onClick={addParticipant}>Add Participant</Button>
              </div>
            </div>
          </div>
        )}
        {/* Edit Participant Modal */}
        {showEditParticipantModal && editingParticipant && (
          <EditParticipantModal
            editingParticipant={editingParticipant}
            onClose={() => {
              setShowEditParticipantModal(false);
              setEditingParticipant(null);
            }}
            onUpdate={updateParticipant}
            setNewParticipantMetadata={setNewParticipantMetadata}
          />
        )}

        <WhatsAppTemplateModal
          isOpen={showTemplateModal}
          onClose={() => setShowTemplateModal(false)}
          onSelectTemplate={handleSelectTemplate}
          participants={participants[selectedConversation as string]}
        />

        <AddConversationModal
          isOpen={showAddConversationModal}
          onOpenChange={setShowAddConversationModal}
          onClose={() => {
            setShowAddConversationModal(false);
            setInitialParticipantForModal(null);
          }}
          onAdd={handleAddConversation}
          initialParticipant={initialParticipantForModal}
        />

        <DeleteConversationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onDelete={handleDeleteConversation}
        />
      </div>

      {showConversationDrawer && (
        <div className="fixed inset-0 z-99 flex">
          {/* Backdrop to close the drawer if clicked outside */}
          <div
            className="bg-opacity-50 h-full w-full bg-black"
            onClick={() => setShowConversationDrawer(false)}
          ></div>

          {/* Sliding Drawer with transition */}
          <div
            className={`fixed top-0 left-0 h-full transform overflow-y-auto bg-white shadow-lg transition-transform duration-300 ease-in-out ${showConversationDrawer ? "translate-x-0" : "-translate-x-full"} w-full`}
          >
            <header className="bg-brick-primary flex items-center justify-between p-4 text-white">
              <h2 className="text-lg font-semibold">Conversations</h2>
              <Button onClick={() => setShowConversationDrawer(false)}>
                Close
              </Button>
            </header>

            {/* Scrollable conversation list */}
            <div className="p-4">
              <ConversationList
                conversations={filteredConversations}
                selectedConversation={selectedConversation}
                setSelectedConversation={(sid: any) => {
                  setSelectedConversation(sid);
                  setShowConversationDrawer(false); // Close the drawer after selection
                }}
                fetchMessages={fetchMessages}
                fetchParticipants={fetchParticipants}
                lastMessagePreviews={lastMessagePreviews}
                lastMessageDates={lastMessageDates}
                timers={timers}
                messages={messages}
                onLoadMore={loadMoreConversations}
                hasMore={!!nextPageToken}
                loadingMore={loadingMore}
                unreadCounts={unreadCounts}
              />
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Image Modal */}
      {fullscreenImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90"
          onClick={() => setFullscreenImage(null)}
        >
          <button
            onClick={() => setFullscreenImage(null)}
            className="absolute top-4 right-4 z-[101] flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            aria-label="Close fullscreen image"
          >
            <X size={24} weight="bold" />
          </button>
          <img
            src={fullscreenImage}
            alt="Fullscreen view"
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default TwilioConversations;
